import React from "react";
import { Mail, ArrowRight } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AuthErrorBanner } from "./AuthErrorBanner";

interface EmailFormProps {
  email: string;
  onEmailChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  error: string | null;
  /** Rendered inside the error banner — the "create an account" hand-off. */
  errorAction?: React.ReactNode;
  /** Rendered at the foot of the card — the standing link across to signup. */
  footer?: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function EmailForm({
  email,
  onEmailChange,
  onSubmit,
  isLoading,
  error,
  errorAction,
  footer,
  title = "Sign in",
  subtitle = "Enter your email to continue",
}: EmailFormProps) {
  return (
    <div className="w-full max-w-md space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-foreground tracking-tight">
          {title}
        </h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>

      {error && <AuthErrorBanner message={error} action={errorAction} />}

      <div className="bg-white rounded-2xl shadow-lg border border-border/50 p-6 sm:p-8 space-y-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full pl-10 pr-3 py-2.5 border border-input rounded-lg text-sm text-foreground placeholder-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors bg-background/50"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full mt-6 group flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-primary from-navy to-royal-blue hover:from-navy/90 hover:to-royal-blue/90 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
            aria-busy={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="h-4 w-4" />
                <span>Sending Code...</span>
              </>
            ) : (
              <>
                <span>Send Code</span>
                <ArrowRight
                  className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                  aria-hidden="true"
                />
              </>
            )}
          </button>
        </form>

        {footer}
      </div>
    </div>
  );
}
